import { AddProject } from "./components/add-project"
import { ProjectRow } from "./components/project-row"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { Box, Paper, Stack } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { createSlot } from "lib/@components/slot/create-slot"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { TabledRepeat } from "lib/@components/tabled-repeat/tabled-repeat"
import { NoResults } from "lib/@components/no-results/no-results"
import { getUserProjects } from "authoring/users/projects/controller/get-user-projects"
import { hash } from "library/hash"
import { ResetTabledRepeat } from "event-definitions"
import { useReferenceState } from "lib/@hooks/use-reference"
import { ProjectPhase } from "authoring/lib/enum/project-phase.mjs"
import { TreeToolbar } from "slot-definitions"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { If } from "lib/@components/switch"
import { ProjectEditDialog } from "./components/project-edit-dialog"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"

export const ProjectListToolbar = createSlot("ProjectListToolbar")

export function ProjectList() {
    const [previousScroll, setPreviousScroll] = useReferenceState("projectListScroll", 0)
    TreeToolbar.usePlug(<AddProject />)
    const scrollRef = useRef(null)
    const [editingProject, setEditingProject] = useState(null)
    const [searchTerm] = useSearchParam("search", "")
    const [sort, setSort] = useReferenceState("projectListSort", "-$modified")
    const [projectPhase, setProjectPhase] = useReferenceState("projectListFilter", null)

    const userProjects = getUserProjects.useResults(
        {
            searchTerm,
            phase: projectPhase || undefined,
            orderStmt: sort,
        },
        withInitialValue(IS_LOADING)
    )

    useEffect(() => {
        ResetTabledRepeat.raiseOnce()
    }, [sort, projectPhase, userProjects])

    useEffect(() => {
        if (previousScroll !== 0 && scrollRef.current) {
            scrollRef.current.scrollTop = Number(previousScroll)
            setPreviousScroll(0)
        }
    })

    if (userProjects === IS_LOADING) return <LoadingScreen />

    const tableHeaders = [
        { title: "Name" },
        { title: "Users", align: "center" },
        { title: "Groups", align: "right" },
        { title: "Schedules", align: "right" },
        { title: "Status" },
        { title: "Last Modified" },
        { title: "" },
    ]

    const ids = userProjects?.projects?.map("id").sort().join(",") || ""
    const key = ids ? hash(ids) : "projectTable"

    return (
        <Bound
            sort={sort}
            setSort={setSort}
            userProjects={userProjects}
            setPreviousScroll={handlePreviousScroll}
            openProjectEdit={handleOpenProjectEdit}
            filter={projectPhase}
            setFilter={setProjectPhase}
            filterOptions={Object.values(ProjectPhase)}
        >
            <PageBreadcrumbContainer heading="Work Packages">
                <Stack spacing={1} sx={{ width: 1, flex: 1, minHeight: 0, mt: 1, overflowY: "auto" }} ref={scrollRef}>
                    <Stack direction="row" alignItems={"center"} spacing={1} sx={{ mb: 1 }}>
                        <Box sx={{ textAlign: "right", flex: 1 }}>
                            <ProjectListToolbar.Slot type="left" />
                        </Box>
                        <ProjectListToolbar.Slot type="right" />
                    </Stack>

                    {userProjects?.projects?.length ? (
                        <TabledRepeat
                            key={key}
                            items={userProjects?.projects}
                            itemPass="project"
                            tableComponent={<Paper sx={{ boxShadow: 3 }} />}
                            itemComponent={<ProjectRow />}
                            tableHeaders={tableHeaders}
                            paged
                            alternating={false}
                        />
                    ) : (
                        <Stack spacing={2} sx={{ alignItems: "center" }}>
                            <NoResults>{"You don't have any work packages yet. Start by adding one."}</NoResults>
                            <Box>
                                <AddProject variant="outlined" />
                            </Box>
                        </Stack>
                    )}
                </Stack>
            </PageBreadcrumbContainer>
            <If value={!!editingProject}>
                <ProjectEditDialog open={!!editingProject} setOpen={handleEditOpen} project={editingProject} />
            </If>
        </Bound>
    )

    function handlePreviousScroll() {
        if (scrollRef.current) {
            setPreviousScroll(Number(scrollRef.current.scrollTop))
        }
    }

    function handleOpenProjectEdit(project) {
        setEditingProject(project)
    }

    function handleEditOpen(isOpen) {
        if (!isOpen) {
            setEditingProject(null)
        }
    }
}
